import { supabase } from "@/integrations/supabase/client";

interface RewardConfigRow {
  is_enabled: boolean;
  coins_per_booking: number;
  coins_per_match: number;
  coins_per_tournament: number;
  organization_id: string;
  [key: string]: unknown;
}

interface PlayerCoinsRow {
  balance: number;
  lifetime_earned: number;
  user_id: string;
  organization_id: string;
  [key: string]: unknown;
}

/**
 * Award coins to a user if the reward system is enabled.
 * Call after booking payment confirmed, match result, or tournament participation.
 */
export async function awardCoins(
  userId: string,
  organizationId: string,
  type: "earned_booking" | "earned_match" | "earned_tournament",
  referenceId?: string,
  description?: string
) {
  // Check if reward system is enabled and get config
  const { data: config } = await supabase
    .from("reward_config" as any)
    .select("*")
    .eq("organization_id", organizationId)
    .maybeSingle();

  const typedConfig = config as RewardConfigRow | null;
  if (!typedConfig || !typedConfig.is_enabled) return;

  const amountMap: Record<string, keyof Pick<RewardConfigRow, "coins_per_booking" | "coins_per_match" | "coins_per_tournament">> = {
    earned_booking: "coins_per_booking",
    earned_match: "coins_per_match",
    earned_tournament: "coins_per_tournament",
  };

  const amount = typedConfig[amountMap[type]] as number ?? 0;
  if (amount <= 0) return;

  // Upsert player_coins
  const { data: existing } = await supabase
    .from("player_coins" as any)
    .select("*")
    .eq("user_id", userId)
    .eq("organization_id", organizationId)
    .maybeSingle();

  const typedExisting = existing as PlayerCoinsRow | null;

  if (typedExisting) {
    await supabase
      .from("player_coins" as any)
      .update({
        balance: (typedExisting.balance ?? 0) + amount,
        lifetime_earned: (typedExisting.lifetime_earned ?? 0) + amount,
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", userId)
      .eq("organization_id", organizationId);
  } else {
    await supabase
      .from("player_coins" as any)
      .insert({
        user_id: userId,
        organization_id: organizationId,
        balance: amount,
        lifetime_earned: amount,
      });
  }

  // Create transaction
  await supabase.from("coin_transactions" as any).insert({
    user_id: userId,
    organization_id: organizationId,
    amount,
    type,
    reference_id: referenceId || null,
    description: description || null,
  });
}
